import React, { useState, useRef } from 'react';
import { loginDestination } from '../utils/coordinatorAccess';
import './Login.css';

function Login() {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const identifierRef = useRef(null);
  const passwordRef = useRef(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const identifier = identifierRef.current?.value.trim() || '';
    const passVal = passwordRef.current?.value || '';

    if (!identifier || !passVal) {
      setError('Please enter your phone number or email and password.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ identifier, password: passVal })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Invalid sign-in details.');
      if (!data.token || !data.user) throw new Error('Sign-in could not be completed. Please try again.');

      // Save user session and auth token
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('token', data.token);
      localStorage.removeItem('selectedCompanyId');
      sessionStorage.clear();

      // Full page redirect — reliable even with browser password-manager popups
      window.location.href = loginDestination(data.user);
      return;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">
      {/* Left Creative Brand Panel */}
      <div className="login-brand-side">
        <div className="brand-glow-circle"></div>
        <div className="brand-content">
          <span className="brand-badge">Enterprise Portal</span>
          <h1>Command & Control Your Holding Operations.</h1>
          <p>Streamline management, monitor real-time company finances, and control access seamlessly from one unified workspace.</p>
          <div className="access-network" aria-hidden="true">
            <div className="access-network-orbit" />
            <svg className="access-network-lines" viewBox="0 0 600 320" preserveAspectRatio="none">
              <path d="M300 160 L95 65 M300 160 L505 65 M300 160 L95 255 M300 160 L505 255" />
            </svg>
            <div className="access-network-center"><span className="access-network-mark">A</span><strong>ACCESS</strong><span>One connected workspace</span></div>
            <div className="access-network-node access-node-people"><span>01 / PEOPLE</span><strong>Your team</strong><div className="access-mini-bars"><i /><i /><i /><i /></div></div>
            <div className="access-network-node access-node-finance"><span>02 / FINANCE</span><strong>Clear visibility</strong><div className="access-mini-chart"><i /><i /><i /><i /><i /></div></div>
            <div className="access-network-node access-node-operations"><span>03 / OPERATIONS</span><strong>Moving together</strong><div className="access-mini-progress"><i /></div></div>
            <div className="access-network-node access-node-growth"><span>04 / GROWTH</span><strong>Room to grow</strong><div className="access-mini-progress"><i /></div></div>
          </div>
        </div>
        <div className="brand-footer-note">© 2026 ACCESS LB Consultancy ERP</div>
      </div>

      {/* Right Interactive Form Panel */}
      <div className="login-form-side">
        <div className="login-card">
          <div className="login-header">
            <img className="login-company-logo" src="/access-lb-consultancy-logo.png" alt="ACCESS LB Consultancy" width="3496" height="869" />
            <h2>Welcome Back</h2>
            <p>Please enter your credentials to access your workspace.</p>
          </div>

          {error && <div className="login-error-banner" role="alert">{error}</div>}

          <form onSubmit={handleLogin} className="login-form-inputs">
            <div className="input-group">
              <label htmlFor="login-identifier">Phone number or email</label>
              <input
                id="login-identifier"
                name="username"
                type="text"
                placeholder="Phone number or name@company.com"
                ref={identifierRef}
                autoComplete="username"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                name="password"
                type="password"
                placeholder="••••••••"
                ref={passwordRef}
                autoComplete="current-password"
                required
              />
            </div>

            <div className="form-actions-row">
              <label className="remember-me">
                <input type="checkbox" /> Remember me
              </label>
              <a href="#forgot" onClick={(e) => e.preventDefault()} className="forgot-link">Forgot password?</a>
            </div>

            <button type="submit" className="btn-login-submit" disabled={loading}>
              {loading ? 'Authenticating...' : 'Sign In to Workspace'}
            </button>
          </form>

          <div className="login-footer-text">
            Access is provided by management. Contact your administrator for sign-in details.
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
